/**
 * Due by month end — "how much do I have to pay before this month is out?"
 *
 * Two figures, kept apart on purpose (see `services/month_end.py`):
 *
 *   * **Printed** — bills a statement printed, still owed, due between today
 *     and the month's last day. The headline.
 *   * **Building** — alert spend on cycles whose *projected* bill also lands
 *     this month. Blue, dashed and chipped like every alert figure, and never
 *     added into the headline: it can only grow until the cycle closes, and the
 *     banks that don't alert leave no trace in it.
 *
 * Bills already past their due date and waiting for a payment to show are left
 * out — the user's ruling: those are usually paid, and counting them would read
 * high every month.
 *
 * The rail under the figure is the month itself, first to last day, with today
 * marked and each bill pinned to its date: how far through the month you are,
 * and how the money is spread across what's left of it.
 */

import { CardSwatch, Panel } from "@/components/ui/primitives";
import { Odometer, stagger } from "@/components/ui/motion";
import type { MonthEndDue, MonthEndLine } from "@/lib/api/client";
import { cn } from "@/lib/cn";
import { formatDate, formatMoney, numberWord } from "@/lib/format";
import { useReducedMotion } from "framer-motion";
import * as React from "react";

const DAY_MS = 86_400_000;
const dayOf = (iso: string) => new Date(`${iso}T00:00:00`).getTime();

export function MonthEndTile({
  data,
  today,
  className,
  index = 3,
}: {
  data: MonthEndDue;
  today: string;
  className?: string;
  index?: number;
}) {
  const building = Number(data.building_total) > 0;
  const daysLeft = Math.round((dayOf(data.month_end) - dayOf(today)) / DAY_MS);
  const monthEndLabel = formatDate(data.month_end).slice(0, 6);

  return (
    <Panel className={cn("animate-rise p-5 sm:p-6", className)} style={stagger(index, 120)}>
      <div className="flex items-baseline justify-between gap-3">
        <span className="label-micro">Due by {monthEndLabel}</span>
        <span className="text-[11.5px] tabular-nums text-faint">
          {daysLeft === 0 ? "last day of the month" : `${daysLeft} day${daysLeft === 1 ? "" : "s"} left`}
        </span>
      </div>

      <div className="mt-4 text-figure-lg font-light text-ink">
        <Odometer value={formatMoney(data.printed_total)} delay={0.5} />
      </div>
      <p className="mt-1.5 text-[13px] text-muted">
        {data.printed_count === 0
          ? `No printed bill falls due before ${monthEndLabel}.`
          : `${capitalise(numberWord(data.printed_count))} printed bill${data.printed_count === 1 ? "" : "s"} still to pay before the month ends.`}
      </p>

      {building && (
        <div
          className="mt-3 flex items-start gap-2 rounded-xl border border-dashed border-live/40 bg-live/[0.05] px-3 py-2"
          title="From bank alerts on cycles that haven't been billed yet, but whose bill is projected to fall due this month. It grows until each cycle closes. Not included in the figure above."
        >
          <span className="mt-[3px] inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-live" aria-hidden />
          <p className="min-w-0 flex-1 text-[12.5px] leading-snug text-muted">
            <span className="font-medium tabular-nums text-live">
              + {formatMoney(data.building_total)}
            </span>{" "}
            building on {numberWord(data.building_count)} cycle
            {data.building_count === 1 ? "" : "s"} that bill before {monthEndLabel}.
          </p>
          <span className="provisional-chip shrink-0">Provisional</span>
        </div>
      )}

      <MonthRail data={data} today={today} />

      {data.lines.length > 0 && (
        <ul className="mt-3 space-y-1.5">
          {data.lines.slice(0, 4).map((line) => (
            <li key={`${line.kind}-${line.card_id}-${line.due_on}`} className="flex items-center gap-2 text-[12.5px]">
              <CardSwatch theme={line.theme} />
              <span className="min-w-0 flex-1 truncate text-muted">
                {line.nickname ?? `•••• ${line.last4}`}
              </span>
              <span className="shrink-0 tabular-nums text-faint">
                {line.projected ? "~" : ""}
                {formatDate(line.due_on).slice(0, 6)}
              </span>
              <span
                className={cn(
                  "w-[6.5rem] shrink-0 text-right tabular-nums",
                  line.kind === "printed" ? "text-ink" : "text-live",
                )}
              >
                {line.kind === "building" ? "+ " : ""}
                {formatMoney(line.amount)}
              </span>
            </li>
          ))}
          {data.lines.length > 4 && (
            <li className="text-[11.5px] text-faint">and {data.lines.length - 4} more</li>
          )}
        </ul>
      )}
    </Panel>
  );
}

/** The month as a rail: elapsed part shaded, today marked, each bill pinned. */
function MonthRail({ data, today }: { data: MonthEndDue; today: string }) {
  const reduce = useReducedMotion();
  const [drawn, setDrawn] = React.useState(false);
  React.useEffect(() => {
    const t = setTimeout(() => setDrawn(true), 500);
    return () => clearTimeout(t);
  }, []);

  const first = `${data.month_end.slice(0, 8)}01`;
  const span = Math.max(1, (dayOf(data.month_end) - dayOf(first)) / DAY_MS);
  const at = (iso: string) => Math.max(0, Math.min(100, ((dayOf(iso) - dayOf(first)) / DAY_MS / span) * 100));
  const todayAt = at(today);

  return (
    <div className="mt-5" aria-hidden>
      <div className="relative h-5">
        <div className="absolute inset-x-0 top-2 h-1 rounded-full bg-raised" />
        <div
          className="absolute left-0 top-2 h-1 rounded-full bg-hairline-strong"
          style={{
            width: drawn || reduce ? `${todayAt}%` : "0%",
            transition: reduce ? "none" : "width 0.9s cubic-bezier(0.16,1,0.3,1)",
          }}
        />
        <span
          className="absolute top-0 h-5 w-0.5 -translate-x-1/2 rounded bg-ink/70"
          style={{ left: `${todayAt}%` }}
        />
        {data.lines.map((line, i) => (
          <Pin key={`${line.kind}-${line.card_id}-${line.due_on}`} line={line} left={at(line.due_on)} index={i} drawn={drawn || !!reduce} />
        ))}
      </div>
      <div className="relative mt-1 h-3 font-mono text-[10px] uppercase tracking-[0.12em] text-faint">
        <span className="absolute left-0">{formatDate(first).slice(0, 6)}</span>
        {todayAt > 14 && todayAt < 86 && (
          <span className="absolute -translate-x-1/2 text-ink/70" style={{ left: `${todayAt}%` }}>
            today
          </span>
        )}
        <span className="absolute right-0">{formatDate(data.month_end).slice(0, 6)}</span>
      </div>
    </div>
  );
}

function Pin({ line, left, index, drawn }: { line: MonthEndLine; left: number; index: number; drawn: boolean }) {
  const theme = line.theme;
  return (
    <span
      className={cn(
        "absolute top-[3px] h-3.5 w-3.5 rounded-full border-2",
        line.kind === "printed" ? "border-brass" : "border-dashed border-live",
      )}
      style={{
        left: `${left}%`,
        backgroundImage: theme
          ? `linear-gradient(140deg, ${theme.gradient_from}, ${theme.gradient_to})`
          : undefined,
        opacity: drawn ? 1 : 0,
        transform: `translateX(-50%) translateY(${drawn ? 0 : -8}px)`,
        transition: `opacity 0.4s ease ${0.6 + index * 0.08}s, transform 0.5s cubic-bezier(0.16,1,0.3,1) ${0.6 + index * 0.08}s`,
      }}
    />
  );
}

function capitalise(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}
